import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Card, Button, Badge, Row, Col, Form, Spinner, Alert } from "react-bootstrap";

import { db, functions } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";

const PAGE_SIZE = 12;

function toDateText(ts) {
  try {
    if (!ts) return "-";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("th-TH");
  } catch {
    return "-";
  }
}

function getStatusBadge(status) {
  if (status === "approved") return "success";
  if (status === "rejected") return "danger";
  if (status === "error") return "warning";
  return "secondary";
}

function getDownloadUrlFromMetadata(item) {
  const token = item?.metadata?.firebaseStorageDownloadTokens || "";
  if (!token || !item?.bucket || !item?.filePath) return "";

  const encodedPath = encodeURIComponent(item.filePath);
  return `https://firebasestorage.googleapis.com/v0/b/${item.bucket}/o/${encodedPath}?alt=media&token=${token}`;
}

function normalizeRole(role) {
  const arr = Array.isArray(role) ? role : [role].filter(Boolean);
  return new Set(arr.map((x) => String(x).toLowerCase()));
}

export default function AdminImageModeration() {
  const { user, userDoc, loading } = useAuth();

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [err, setErr] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [status, setStatus] = useState("rejected"); // rejected | error | approved | all
  const [pathType, setPathType] = useState("all"); // all | users | payment-slips | courses

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const canAccess = useMemo(() => {
    const roles = normalizeRole(userDoc?.role);
    return roles.has("admin");
  }, [userDoc]);

  const buildQueryParts = ({ withCursor = false } = {}) => {
    const parts = [];

    if (status !== "all") {
      parts.push(where("status", "==", status));
    }

    parts.push(orderBy("createdAt", "desc"));

    if (withCursor && cursor) {
      parts.push(startAfter(cursor));
    }

    parts.push(limit(PAGE_SIZE));
    return parts;
  };

  const loadFirst = async () => {
    setErr("");
    setSuccessMsg("");
    setLoadingList(true);
    setCursor(null);
    setHasMore(true);

    try {
      const q = query(collection(db, "imageModerationLogs"), ...buildQueryParts());
      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (pathType !== "all") {
        rows = rows.filter((it) => String(it.filePath || "").startsWith(`${pathType}/`));
      }

      setItems(rows);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดรายการรูปภาพไม่สำเร็จ");
    } finally {
      setLoadingList(false);
    }
  };

  const loadMore = async () => {
    if (!cursor || !hasMore) return;

    setErr("");
    setLoadingMore(true);

    try {
      const q = query(
        collection(db, "imageModerationLogs"),
        ...buildQueryParts({ withCursor: true })
      );

      const snap = await getDocs(q);
      let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (pathType !== "all") {
        rows = rows.filter((it) => String(it.filePath || "").startsWith(`${pathType}/`));
      }

      setItems((prev) => [...prev, ...rows]);
      setCursor(snap.docs[snap.docs.length - 1] || cursor);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดเพิ่มไม่สำเร็จ");
    } finally {
      setLoadingMore(false);
    }
  };

  const onDeleteFile = async (item) => {
    if (!item?.id || !item?.filePath || !item?.bucket) return;

    const ok = window.confirm(
      `ต้องการลบไฟล์นี้ออกจาก Storage ใช่ไหม?\n\n${item.filePath}`
    );

    if (!ok) return;

    setErr("");
    setSuccessMsg("");
    setDeletingId(item.id);

    try {
      const fn = httpsCallable(functions, "adminDeleteModeratedImage");

      await fn({
        logId: item.id,
        filePath: item.filePath,
        bucket: item.bucket,
      });

      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                action: "deleted",
                deletedAt: new Date(),
              }
            : x
        )
      );

      setSuccessMsg("ลบไฟล์เรียบร้อยแล้ว");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "ลบไฟล์ไม่สำเร็จ");
    } finally {
      setDeletingId("");
    }
  };

  useEffect(() => {
    if (!user?.uid || !canAccess) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, canAccess, status, pathType]);

  if (loading) return null;
  if (!user) return null;
  if (!canAccess) return <Alert variant="warning">หน้านี้สำหรับแอดมินเท่านั้น</Alert>;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-0">ตรวจรูปภาพ</h4>
          <div className="text-muted small">
            รายการจาก <code>imageModerationLogs</code>
          </div>
        </div>

        <Button variant="outline-secondary" onClick={loadFirst} disabled={loadingList || loadingMore}>
          รีเฟรช
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}
      {successMsg && <Alert variant="success">{successMsg}</Alert>}

      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="rejected">Rejected</option>
                <option value="error">Error</option>
                <option value="approved">Approved</option>
                <option value="all">ทั้งหมด</option>
              </Form.Select>
            </Col>

            <Col md={5}>
              <Form.Label>ประเภท path</Form.Label>
              <Form.Select value={pathType} onChange={(e) => setPathType(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="users">users / รูปโปรไฟล์</option>
                <option value="payment-slips">payment-slips / สลิป</option>
                <option value="courses">courses / รูปคอร์ส</option>
              </Form.Select>
            </Col>

            <Col md={2} className="d-grid">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setStatus("rejected");
                  setPathType("all");
                }}
              >
                ล้าง
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loadingList ? (
        <div className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลดรายการ…
        </div>
      ) : items.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center py-5">
            <div className="fw-semibold mb-1">ยังไม่มีรายการ</div>
            <div className="text-muted small">ไม่พบรูปภาพตามตัวกรองนี้</div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {items.map((item) => {
              const imageUrl = getDownloadUrlFromMetadata(item);
              const deleted = item.action === "deleted";

              return (
                <Col key={item.id} xs={12} md={6} lg={4}>
                  <Card className="border h-100">
                    <div
                      style={{
                        width: "100%",
                        aspectRatio: "16/9",
                        background: "rgba(127,127,127,.08)",
                        borderTopLeftRadius: 6,
                        borderTopRightRadius: 6,
                        overflow: "hidden",
                      }}
                    >
                      {imageUrl && !deleted ? (
                        <img
                          src={imageUrl}
                          alt={item.filePath || "moderated file"}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                          {deleted ? "ไฟล์ถูกลบแล้ว" : "ไม่มี preview"}
                        </div>
                      )}
                    </div>

                    <Card.Body className="d-flex flex-column">
                      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                        <Badge bg={getStatusBadge(item.status)}>
                          {item.status || "unknown"}
                        </Badge>

                        <Badge bg="light" text="dark" style={{ border: "1px solid rgba(0,0,0,.12)" }}>
                          {item.action || "none"}
                        </Badge>
                      </div>

                      <div className="small text-muted mb-2">
                        {toDateText(item.createdAt)}
                      </div>

                      <div
                        className="small"
                        style={{ wordBreak: "break-all", lineHeight: 1.35 }}
                      >
                        {item.filePath}
                      </div>

                      <div className="mt-3 small">
                        <div>adult: <strong>{item.safeSearch?.adult || "-"}</strong></div>
                        <div>violence: <strong>{item.safeSearch?.violence || "-"}</strong></div>
                        <div>racy: <strong>{item.safeSearch?.racy || "-"}</strong></div>
                      </div>

                      {item.errorMessage && (
                        <Alert variant="warning" className="small mt-3 mb-0">
                          {item.errorMessage}
                        </Alert>
                      )}

                      <div className="mt-auto pt-3 d-flex gap-2">
                        {imageUrl && !deleted && (
                          <Button
                            as="a"
                            href={imageUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="outline-primary"
                            className="w-100"
                          >
                            เปิดรูป
                          </Button>
                        )}

                        <Button
                          variant="outline-danger"
                          className="w-100"
                          onClick={() => onDeleteFile(item)}
                          disabled={deleted || deletingId === item.id}
                        >
                          {deletingId === item.id ? "กำลังลบ..." : deleted ? "ลบแล้ว" : "ลบไฟล์"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <div className="d-flex justify-content-center mt-4">
            {hasMore ? (
              <Button variant="outline-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่ม"}
              </Button>
            ) : (
              <div className="text-muted small">แสดงครบแล้ว</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}